/**
 * Motor de pagos — ejecuta la máquina de estados de pago sobre la reserva,
 * con el cliente inyectado. Hace cumplir las reglas de ADR-0002 (autorizar →
 * capturar / anular) y ADR-0003 (offline solo en tarifas reembolsables).
 */
import type { Reservation, PaymentStatus } from './types'
import { assertTransitionPayment } from './reservations'
import type { PaymentClient } from './payments'

function setPay(
  res: Reservation,
  status: PaymentStatus,
  patch: Partial<Reservation> = {},
): Reservation {
  const from: PaymentStatus = res.paymentStatus ?? 'pago_requerido'
  assertTransitionPayment(from, status)
  return { ...res, ...patch, paymentStatus: status }
}

/** Marca la reserva como pendiente de pago (estado inicial del pago). */
export function requirePayment(res: Reservation): Reservation {
  return { ...res, paymentStatus: 'pago_requerido' }
}

/* ============== Riel tarjeta (MercadoPago, ADR-0002) ============== */

/** Autoriza (retiene) el pago. Se hace ANTES de reservar. */
export async function authorizeCard(
  client: PaymentClient,
  res: Reservation,
  idempotencyKey: string,
): Promise<Reservation> {
  const r = await client.authorize(res.price ?? 0, res.currency ?? 'MXN', idempotencyKey)
  if (r.status === 'rejected') {
    return setPay(res, 'rechazado', { paymentRail: 'tarjeta' })
  }
  return setPay(res, 'autorizado', { paymentRail: 'tarjeta', mpPaymentId: r.paymentId })
}

/** Captura (cobra) tras confirmar la reserva. */
export async function captureCard(client: PaymentClient, res: Reservation): Promise<Reservation> {
  await client.capture(res.mpPaymentId!, res.price ?? 0)
  return setPay(res, 'capturado')
}

/** Anula la retención (la reserva falló → el cliente no paga). */
export async function voidCard(client: PaymentClient, res: Reservation): Promise<Reservation> {
  await client.voidAuthorization(res.mpPaymentId!)
  return setPay(res, 'anulado')
}

/* ============== Riel offline (transferencia, ADR-0003) ============== */

/**
 * Inicia el pago offline. REGLA (ADR-0003): solo en tarifas reembolsables —
 * reservamos y esperamos; si no llega el pago, cancelamos sin penalización.
 */
export async function startOfflinePayment(
  client: PaymentClient,
  res: Reservation,
): Promise<Reservation> {
  if (res.refundable !== true) {
    throw new Error('Pago offline no permitido en tarifas no reembolsables (ADR-0003)')
  }
  const r = await client.createOfflineReference(res.price ?? 0, res.currency ?? 'MXN', res.id)
  return setPay(res, 'pago_offline_pendiente', { paymentRail: 'offline', offlineReference: r.reference })
}

/** Concilia contra los movimientos bancarios. Si hay match → pagado. */
export async function reconcileOffline(
  client: PaymentClient,
  res: Reservation,
): Promise<Reservation> {
  const r = await client.matchMovement(res.offlineReference!, res.price ?? 0)
  if (r.matched) {
    return setPay(res, 'pagado_offline')
  }
  return res // sigue pendiente
}

/** Vence la ventana de 24h sin conciliar → el cron cancela la reserva. */
export function expireOffline(res: Reservation): Reservation {
  return setPay(res, 'expirado')
}
